/* ---------------------------------------------------------------------------
 * KOM | Tracking caroselli
 * assets/kom-tracking.js
 *
 * Misura in aggregato quanto ogni carosello contribuisce a click, add-to-cart
 * e fatturato. Due livelli indipendenti:
 *
 *   A) Attribuzione fatturato — scrive l'attributo di carrello
 *      `carousel_sources` (CSV dedup). Shopify lo porta in
 *      order.note_attributes. Attivo SEMPRE, nessuna infrastruttura.
 *
 *   B) Funnel (click / impression / add-to-cart) — manda beacon a un endpoint
 *      HTTP esterno. Si accende SOLO se il setting di tema
 *      `carousel_tracking_endpoint` è compilato. A endpoint vuoto: nessun
 *      beacon e NESSUN COOKIE.
 *
 * Non modifica in alcun modo il bundle del tema (assets/theme.js): si aggancia
 * agli eventi pubblici `variant:add` e `cart:change` emessi da Horizon.
 * ------------------------------------------------------------------------- */

(() => {
  'use strict';

  if (window.__komCarouselTracking) return; // idempotente
  window.__komCarouselTracking = true;

  // -------------------------------------------------------------------------
  // Configurazione
  // -------------------------------------------------------------------------

  const ENDPOINT = String(window.__carouselTrackingEndpoint || '').trim();
  const FUNNEL = ENDPOINT !== ''; // livello B attivo?

  const SOURCE_SELECTOR = '[data-carousel-source]';
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';

  const MEMORY_KEY = 'kom:carousel-sources'; // localStorage: handle -> {source, ts}
  const VIEWS_KEY = 'kom:carousel-views'; // sessionStorage: dedup impression
  const COOKIE_NAME = 'vv_sid'; // id sessione (solo a funnel attivo)

  const MEMORY_TTL = 3 * 24 * 60 * 60 * 1000; // click-through: 3 giorni
  const COOKIE_TTL = 30 * 24 * 60 * 60; // secondi
  const MAX_SOURCES = 5; // il lettore a valle scarta oltre la 5ª
  const MAX_SOURCE_LEN = 64; // e oltre i 64 caratteri

  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // -------------------------------------------------------------------------
  // Utility
  // -------------------------------------------------------------------------

  /**
   * Normalizza una sorgente allo slug accettato dal lettore: a-z 0-9 : _ -
   * Rete di sicurezza: in Liquid le etichette passano già da `| handle`, ma un
   * data-carousel-source scritto a mano non deve produrre parole appiccicate.
   */
  function slug(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9:_-]+/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, MAX_SOURCE_LEN)
      .replace(/-+$/, '');
  }

  /** Handle prodotto da un href, tollerante ai prefissi di lingua (/de/, /en/). */
  function handleFromHref(href) {
    if (!href) return null;
    const match = String(href).match(/\/products\/([^/?#]+)/);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]).toLowerCase();
    } catch (error) {
      return match[1].toLowerCase();
    }
  }

  /** Posizione (1-based) della card cliccata dentro il wrapper del carosello. */
  function cardPosition(node, wrapper) {
    let current = node;
    while (current && current.parentElement && current.parentElement !== wrapper) {
      current = current.parentElement;
    }
    if (!current || current.parentElement !== wrapper) return null;
    return Array.prototype.indexOf.call(wrapper.children, current) + 1;
  }

  // -------------------------------------------------------------------------
  // Identità di sessione — SOLO a funnel attivo (a endpoint vuoto: nessun cookie)
  // -------------------------------------------------------------------------

  function sessionId() {
    if (!FUNNEL) return null;

    const existing = document.cookie.match(
      new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]+)')
    );
    if (existing) return existing[1];

    let id;
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      id = window.crypto.randomUUID();
    } else {
      id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    }

    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      COOKIE_NAME + '=' + id + '; path=/; max-age=' + COOKIE_TTL + '; SameSite=Lax' + secure;
    return id;
  }

  // -------------------------------------------------------------------------
  // Memoria sorgente (finestra click-through 3 giorni)
  // -------------------------------------------------------------------------

  function readMemory() {
    try {
      const raw = localStorage.getItem(MEMORY_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  function writeMemory(map) {
    try {
      localStorage.setItem(MEMORY_KEY, JSON.stringify(map));
    } catch (error) {
      /* storage pieno o disabilitato: il tracking degrada, il sito no */
    }
  }

  /** Rimuove le voci oltre la finestra di 3 giorni. */
  function pruneMemory(map) {
    const now = Date.now();
    Object.keys(map).forEach((key) => {
      const entry = map[key];
      if (!entry || !entry.ts || now - entry.ts > MEMORY_TTL) delete map[key];
    });
    return map;
  }

  function remember(handle, source) {
    if (!handle || !source) return;
    const map = pruneMemory(readMemory());
    map[handle] = { source: source, ts: Date.now() };
    writeMemory(map);
  }

  function recall(handle) {
    if (!handle) return null;
    const map = pruneMemory(readMemory());
    writeMemory(map);
    const entry = map[handle];
    return entry ? slug(entry.source) || null : null;
  }

  // -------------------------------------------------------------------------
  // Beacon (livello B) — inerte a endpoint vuoto
  // -------------------------------------------------------------------------

  function beacon(payload) {
    if (!FUNNEL) return;

    const body = JSON.stringify(
      Object.assign({ session_id: sessionId(), ts: Date.now() }, payload)
    );

    // text/plain: con sendBeacon un application/json scatenerebbe il preflight CORS.
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([body], { type: 'text/plain' });
        if (navigator.sendBeacon(ENDPOINT, blob)) return;
      }
    } catch (error) {
      /* fallback sotto */
    }

    try {
      fetch(ENDPOINT, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body: body,
      }).catch(() => {});
    } catch (error) {
      /* mai far fallire la navigazione per un beacon */
    }
  }

  // -------------------------------------------------------------------------
  // Click sui prodotti dentro un carosello
  // -------------------------------------------------------------------------

  // In capture: i caroselli drag-abilitati di Horizon possono fermare la
  // propagazione del click, e le card Clerk arrivano dopo il primo render.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      const link = target.closest(PRODUCT_LINK_SELECTOR);
      if (!link) return;

      const wrapper = link.closest(SOURCE_SELECTOR);
      if (!wrapper) return;

      const source = slug(wrapper.getAttribute('data-carousel-source'));
      if (!source) return;

      const handle = handleFromHref(link.getAttribute('href'));
      if (!handle) return;

      remember(handle, source);

      beacon({
        event: 'carousel_click',
        source: source,
        product_handle: handle,
        position: cardPosition(link, wrapper),
        page: location.pathname,
      });
    },
    { capture: true, passive: true }
  );

  // -------------------------------------------------------------------------
  // Impression (livello B) — nessun observer se il funnel è spento
  // -------------------------------------------------------------------------

  const observed = new WeakSet();
  const zeroSized = new WeakSet(); // wrapper ancora senza box (es. span Clerk vuoto)
  const viewedInMemory = new Set();
  let impressionObserver = null;

  function firstViewOfSession(key) {
    if (viewedInMemory.has(key)) return false;
    viewedInMemory.add(key);
    try {
      const raw = sessionStorage.getItem(VIEWS_KEY);
      const list = raw ? JSON.parse(raw) : [];
      if (Array.isArray(list)) {
        if (list.indexOf(key) !== -1) return false;
        list.push(key);
        sessionStorage.setItem(VIEWS_KEY, JSON.stringify(list));
      }
    } catch (error) {
      /* dedup solo in memoria */
    }
    return true;
  }

  function scanForImpressions() {
    if (!impressionObserver) return;

    document.querySelectorAll(SOURCE_SELECTOR).forEach((element) => {
      const known = observed.has(element);
      const hasBox = element.getBoundingClientRect().height > 0;

      // Già osservato e già misurabile: niente da fare.
      if (known && !(zeroSized.has(element) && hasBox)) return;

      // Era ad altezza 0 (Clerk non ancora renderizzato) e ora ha un box:
      // ri-osservalo per forzare una nuova misurazione.
      if (known) impressionObserver.unobserve(element);

      observed.add(element);
      if (hasBox) zeroSized.delete(element);
      else zeroSized.add(element);
      impressionObserver.observe(element);
    });
  }

  function initImpressions() {
    if (!FUNNEL || !('IntersectionObserver' in window)) return;

    impressionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          // Elemento senza area: contenuto non ancora iniettato, si riprova dopo.
          const box = entry.boundingClientRect;
          if (box.width * box.height <= 0) return;

          const source = slug(entry.target.getAttribute('data-carousel-source'));
          impressionObserver.unobserve(entry.target);
          zeroSized.delete(entry.target);
          if (!source) return;

          if (!firstViewOfSession(source + '|' + location.pathname)) return;

          beacon({
            event: 'carousel_view',
            source: source,
            page: location.pathname,
          });
        });
      },
      { threshold: 0.25 }
    );

    scanForImpressions();

    // I caroselli di Horizon arrivano in DOM in momenti diversi:
    // product-recommendations e recently-viewed-products iniettano via fetch,
    // Clerk.io inietta le card dopo il proprio render, e nell'editor le sezioni
    // vengono ridisegnate. Un unico MutationObserver copre tutti i casi.
    let queued = false;
    const mutationObserver = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        scanForImpressions();
      });
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------------
  // Add-to-cart
  // -------------------------------------------------------------------------

  // Horizon emette `variant:add` sul form (bubbling) e subito dopo `cart:change`
  // su documentElement con il carrello completo. Il primo dà il form (quindi
  // handle e eventuale carosello contenitore), il secondo dà cart.attributes e
  // cart.token: serve leggerli entrambi.
  let pendingAdd = null;

  document.addEventListener(
    'variant:add',
    (event) => {
      const target = event.target;
      const form = target && typeof target.closest === 'function' ? target.closest('form') : null;
      const detail = event.detail || {};
      pendingAdd = {
        form: form,
        items: Array.isArray(detail.items) ? detail.items : null,
      };
    },
    true
  );

  document.documentElement.addEventListener('cart:change', (event) => {
    const detail = event.detail || {};

    // Ignora line-item:change e quick-order-list:add.
    if (detail.baseEvent !== 'variant:add') return;

    const context = pendingAdd;
    pendingAdd = null;
    if (!context) return;

    const cart = detail.cart || null;
    const handle = resolveHandle(context);
    const source = resolveSource(context, handle);
    if (!source) return;

    writeCartAttributes(source, cart);

    beacon({
      event: 'carousel_add_to_cart',
      source: source,
      product_handle: handle,
      page: location.pathname,
      cart_token: (cart && cart.token) || null,
    });
  });

  function resolveHandle(context) {
    const form = context.form;

    if (form && form.dataset && form.dataset.productHandle) {
      return form.dataset.productHandle.toLowerCase();
    }

    // Risposta di /cart/add.js: le line item portano già lo handle.
    if (context.items && context.items.length && context.items[0].handle) {
      return String(context.items[0].handle).toLowerCase();
    }

    // Ultima spiaggia: la product-card che avvolge il form (quick-add).
    const card = form && form.closest ? form.closest('product-card[handle]') : null;
    if (card) return String(card.getAttribute('handle')).toLowerCase();

    return null;
  }

  function resolveSource(context, handle) {
    // 1. Add-to-cart avvenuto DENTRO un carosello (quick-add o quick-buy modal):
    //    la sorgente è diretta, senza passare dalla memoria.
    const form = context.form;
    if (form && form.closest) {
      const wrapper = form.closest(SOURCE_SELECTOR);
      if (wrapper) {
        const direct = slug(wrapper.getAttribute('data-carousel-source'));
        if (direct) return direct;
      }
    }

    // 2. Altrimenti: click precedente su un carosello, entro 3 giorni.
    return recall(handle);
  }

  /**
   * Scrive SOLO le nostre chiavi. /cart/update.js fonde gli attributi passati e
   * lascia intatti gli altri (products_mobile_grid_mode / products_desktop_grid_mode
   * usati dal tema per lo stato della griglia): non inviare mai l'oggetto completo.
   */
  function writeCartAttributes(source, cart) {
    const attributes = (cart && cart.attributes) || {};
    const current = String(attributes.carousel_sources || '');

    const sources = current
      .split(',')
      .map(slug)
      .filter(Boolean);

    if (sources.indexOf(source) === -1) sources.push(source);

    // Il lettore a valle scarta in silenzio oltre la 5ª sorgente: teniamo le
    // prime cinque (le più vicine all'inizio del percorso).
    const value = sources.slice(0, MAX_SOURCES).join(',');

    const payload = { carousel_sources: value };

    const sid = sessionId(); // null a funnel spento -> nessuna chiave di join
    if (sid) payload.carousel_session = sid;

    const alreadyCorrect =
      value === current && (!sid || String(attributes.carousel_session || '') === sid);
    if (alreadyCorrect) return;

    try {
      fetch(routeRoot() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: payload }),
      }).catch(() => {});
    } catch (error) {
      /* l'attribuzione è best-effort: mai bloccare il carrello */
    }
  }

  // -------------------------------------------------------------------------
  // Avvio + piccolo helper di verifica manuale in console
  // -------------------------------------------------------------------------

  initImpressions();

  window.KomCarouselTracking = {
    endpoint: ENDPOINT,
    funnelEnabled: FUNNEL,
    memory: () => pruneMemory(readMemory()),
    sources: () =>
      Array.from(document.querySelectorAll(SOURCE_SELECTOR)).map((element) =>
        element.getAttribute('data-carousel-source')
      ),
  };
})();
